import { AbstractControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// shared by the add-user dialogs (super admin, operator admin, branch staff):
// look a family up by family code (GET /devotee/fc/:familyCode) and offer its
// members as the Name picker. Picking a name supplies fullName AND the SSO
// uuid (memUuid) — neither is typed by hand.
export type FamilyMember = {
  memUuid: string | null;
  memberCode: string | null;
  fullName: string;
};

export class FamilyMemberLookup {
  members: FamilyMember[] = [];
  loaded: boolean = false;
  isLoading: boolean = false;

  // state of the PICKED member: does that person already hold an account?
  pickStatus: 'idle' | 'checking' | 'ok' | 'taken' = 'idle';

  private sub?: Subscription;
  private lastPicked = '';

  // fetch = the /devotee/fc/:familyCode call; checkMember (optional) = the
  // /common/member-availability call used to validate a picked name at once.
  constructor(
    private readonly fetch: (familyCode: string) => Observable<any>,
    private readonly checkMember?: (memUuid: string) => Observable<any>
  ) {}

  get pickMessage(): string {
    switch (this.pickStatus) {
      case 'checking':
        return 'Checking this person…';
      case 'taken':
        return 'This person already has a user account.';
      default:
        return '';
    }
  }

  // called when a name is picked: refuse a person who is already a user.
  verifyPick(memUuid: string): void {
    if (!this.checkMember || !memUuid) {
      this.pickStatus = 'idle';
      return;
    }
    this.pickStatus = 'checking';
    this.lastPicked = memUuid;
    this.checkMember(memUuid).subscribe({
      next: (res: any) => {
        // ignore an answer for an older pick
        if (this.lastPicked !== memUuid) return;
        this.pickStatus = res?.data?.available ? 'ok' : 'taken';
      },
      error: () => {
        if (this.lastPicked !== memUuid) return;
        // let the submit-time guard decide; do not block the dialog on a glitch
        this.pickStatus = 'idle';
      },
    });
  }

  // watch the family-code control: as soon as it holds exactly 12 digits (and
  // the admin pauses), the members load by themselves — no Find button.
  attach(control: AbstractControl, onError: (message: string) => void, onLoaded: () => void): void {
    this.sub = control.valueChanges.pipe(debounceTime(400)).subscribe((value) => {
      const code = String(value ?? '').trim();
      if (/^\d{12}$/.test(code)) {
        this.find(code, onError, onLoaded);
      } else {
        this.reset();
      }
    });
  }

  destroy(): void {
    this.sub?.unsubscribe();
  }

  // a changed family code invalidates the loaded list
  reset(): void {
    this.members = [];
    this.loaded = false;
    this.pickStatus = 'idle';
  }

  find(familyCode: string, onError: (message: string) => void, onLoaded: () => void): void {
    const code = String(familyCode ?? '').trim();
    if (!code) {
      onError('Enter a family code first.');
      return;
    }
    this.isLoading = true;
    this.fetch(code).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        this.members = rows.map((m: any) => ({
          memUuid: m.memUuid ?? null,
          memberCode: m.memberCode ?? null,
          fullName: m.fullName || [m.firstName, m.lastName].filter(Boolean).join(' ') || '-',
        }));
        this.loaded = true;
        this.isLoading = false;
        if (this.members.length === 0) onError('No members found for this family code.');
        onLoaded();
      },
      error: (err: any) => {
        this.isLoading = false;
        onError(err?.error?.message || 'Failed to fetch family members.');
      },
    });
  }

  fullNameOf(memUuid: string): string {
    return this.members.find((m) => m.memUuid === memUuid)?.fullName ?? '';
  }
}
